import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { SectionTitle, ProvenanceFooter, Chip, BandPill, ProvenanceBadge } from '../components/ui/kits.jsx';
import { fmt, fmtInt, bandColor, CRIT_COLOR, CRIT_ORDER, TOOL_COLOR } from '../lib/format.js';
import { ArrowRight, IndianRupee, Users, Activity, Target, Trophy } from 'lucide-react';

const STAGES = [
  { key: 'inputs',     title: 'Inputs',    icon: IndianRupee, color: '#042a54', desc: 'Resources deployed' },
  { key: 'activities', title: 'Activities', icon: Activity,   color: '#0a63b0', desc: 'Interventions delivered' },
  { key: 'outputs',    title: 'Outputs',   icon: Target,      color: '#2e7cbf', desc: 'Direct products of activities' },
  { key: 'outcomes',   title: 'Outcomes',  icon: Users,       color: '#e23d28', desc: 'Board-level ambitions' },
  { key: 'impact',     title: 'Impact',    icon: Trophy,      color: '#9b2d20', desc: 'Long-term change' },
];

export default function ResultsChain() {
  const { data, resultsChain: rc } = useData();
  if (!data || !rc) {
    return (
      <div style={{ padding: 40, color: 'var(--ink-3)', fontSize: 14 }} data-testid="page-results-loading">
        Loading results chain…
      </div>
    );
  }

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-results">
      {/* Stage header strip */}
      <div className="card" style={{ padding: 18, overflow: 'auto' }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="5-stage logic chain derived from OECD data" />}>Programme Theory of Change</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr auto 1fr', gap: 8, alignItems: 'stretch', minWidth: 800 }}>
          {STAGES.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={{
                padding: 14,
                background: `linear-gradient(140deg, ${s.color}, ${s.color}dd)`,
                color: '#fff', borderRadius: 12, minHeight: 100,
                display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'center',
                boxShadow: 'var(--shadow-sm)',
              }}>
                <s.icon size={18} style={{ opacity: .8 }} />
                <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{s.title}</div>
                <div style={{ fontSize: 10.5, opacity: .8, lineHeight: 1.3 }}>{s.desc}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--ink-4)' }}>
                  <ArrowRight size={18} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="card" style={{ padding: 18 }} data-testid="section-inputs">
        <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ProvenanceBadge kind="live" /><Chip color="#042a54">Stage 1</Chip></div>}>Inputs · resources deployed</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {rc.inputs.map((inp, i) => (
            <div key={i} style={{
              padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)',
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em' }}>{inp.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', margin: '4px 0' }} className="tabular">{inp.value}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{inp.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activities */}
      <div className="card" style={{ padding: 18 }} data-testid="section-activities">
        <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ProvenanceBadge kind="live" /><Chip color="#0a63b0">Stage 2 · {rc.activities.length} interventions</Chip></div>}>
          Activities · interventions delivered
        </SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rc.activities.map((a) => (
            <div key={a.name} style={{
              display: 'grid', gridTemplateColumns: '1fr auto 200px 60px',
              gap: 10, alignItems: 'center',
              padding: '8px 12px', border: '1px solid var(--line)',
              borderRadius: 8, background: 'var(--surface-2)',
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{a.name}</div>
              <Chip color={TOOL_COLOR[a.tool === 'H&H' ? 'Health' : (a.tool === 'PoE' ? 'POE' : a.tool)]}>{a.tool}</Chip>
              <div style={{ position: 'relative', height: 8, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
                <div className="anim-grow" style={{
                  width: `${a.score ? Math.max(0, Math.min(100, (a.score / 5) * 100)) : 0}%`,
                  height: '100%', background: bandColor(a.score >= 4.6 ? 'Excellent' : a.score >= 3.6 ? 'Good' : a.score >= 2.6 ? 'Moderate' : 'Poor'),
                }} />
              </div>
              <div className="tabular" style={{ fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{fmt(a.score, 2)}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          Interventions ranked by average OECD score across projects that reported them.
        </div>
      </div>

      {/* Outputs */}
      <div className="card" style={{ padding: 18 }} data-testid="section-outputs">
        <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ProvenanceBadge kind="report" note="Attainment from HDFC Parivartan Report FY24-25" /><Chip color="#2e7cbf">Stage 3 · {rc.outputs.length} output pillars</Chip></div>}>
          Outputs · KPI attainment by pillar (approved MIS rows, FY 2023-24 & 2024-25)
        </SectionTitle>
        <table className="pv-table">
          <thead>
            <tr>
              <th>Pillar</th>
              <th style={{ textAlign: 'right' }}>Target</th>
              <th style={{ textAlign: 'right' }}>Actual (cumul.)</th>
              <th>Attainment</th>
              <th style={{ textAlign: 'right' }}>KPIs</th>
              <th style={{ textAlign: 'right' }}>Projects</th>
            </tr>
          </thead>
          <tbody>
            {rc.outputs.map((p) => {
              const pct = p.attainmentPct ?? 0;
              const displayPct = Math.min(200, pct);
              const color = pct >= 100 ? '#1f8a4c' : pct >= 60 ? '#5aa06a' : pct >= 30 ? '#d98a1f' : '#c8452f';
              return (
                <tr key={p.name} data-testid={`output-${p.name.slice(0, 20)}`}>
                  <td style={{ fontSize: 12.5, fontWeight: 600 }}>{p.name}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{fmtInt(p.target)}</td>
                  <td className="tabular" style={{ textAlign: 'right', color: 'var(--ink-2)' }}>{fmtInt(p.actualCumulative)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 160 }}>
                      <div style={{ flex: 1, height: 8, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                        <div className="anim-grow" style={{
                          width: `${Math.max(0, Math.min(100, displayPct / 2))}%`,
                          height: '100%', background: color, borderRadius: 4,
                        }} />
                        <div style={{
                          position: 'absolute', top: 0, bottom: 0, left: '50%',
                          borderLeft: '1.5px dashed var(--ink-4)', opacity: .5,
                        }} title="100% target line" />
                      </div>
                      <div className="tabular" style={{ fontSize: 11.5, fontWeight: 800, color, minWidth: 55, textAlign: 'right' }}>
                        {fmt(pct, 0)}%
                      </div>
                    </div>
                  </td>
                  <td className="tabular" style={{ textAlign: 'right', color: 'var(--ink-3)' }}>{p.kpiCount}</td>
                  <td className="tabular" style={{ textAlign: 'right', color: 'var(--ink-3)' }}>{p.projectCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          Attainment = Σ(Q1..Q4 actuals) ÷ Σ(targets). Bar caps at 100% target line (dashed); overshoots (e.g. Community Institutions) shown in the label.
        </div>
      </div>

      {/* Outcomes */}
      <div className="card" style={{ padding: 18 }} data-testid="section-outcomes">
        <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ProvenanceBadge kind="report" /><Chip color="#e23d28">Stage 4 · {rc.outcomes.length} board ambitions</Chip></div>}>
          Outcomes · board-level ambitions
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
          {rc.outcomes.map((o, i) => (
            <div key={i} style={{
              padding: 12, borderLeft: '3px solid var(--accent)',
              background: 'var(--surface-2)', borderRadius: 8,
              border: '1px solid var(--line)', borderLeftWidth: 3,
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>
                {o.focus}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>{o.goal}</div>
              {o.milestone && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#1f8a4c', fontWeight: 600 }}>
                  ▲ {o.milestone}
                </div>
              )}
              {o.projects != null && (
                <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--ink-4)' }} className="tabular">
                  {o.projects} project{o.projects === 1 ? '' : 's'} contributing
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Impact */}
      <div className="card" style={{ padding: 18 }} data-testid="section-impact">
        <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ProvenanceBadge kind="live" /><Chip color="#9b2d20">Stage 5 · OECD verdict</Chip></div>}>
          Impact · long-term change
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          <div style={{
            padding: 16, borderRadius: 12,
            background: 'linear-gradient(140deg, #042a54, #004c8f)',
            color: '#fff',
          }} data-testid="impact-programme">
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', opacity: .8 }}>Programme Overall</div>
            <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1 }} className="tabular">{fmt(rc.impact.programmeOverall, 2)}</div>
            <BandPill band={rc.impact.programmeBand} />
            <div style={{ fontSize: 11, opacity: .8, marginTop: 6 }}>Weighted across all OECD criteria</div>
          </div>

          <div style={{ padding: 16, borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--line)' }} data-testid="impact-sdg">
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--ink-4)' }}>SDGs Advanced</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#e23d28' }} className="tabular">{rc.impact.sdgsAdvanced}<span style={{ fontSize: 16, color: 'var(--ink-4)' }}> / {rc.impact.sdgsTotal}</span></div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>UN Sustainable Development Goals</div>
          </div>

          <div style={{ padding: 16, borderRadius: 12, background: 'var(--surface-2)', border: '1px solid var(--line)' }} data-testid="impact-lives">
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--ink-4)' }}>Lives Impacted</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--primary)' }} className="tabular">{fmt(rc.impact.livesImpactedCr, 2)}<span style={{ fontSize: 16, color: 'var(--ink-4)' }}> cr</span></div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Cumulative programme reach</div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>Per criterion</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
            {CRIT_ORDER.map((c) => {
              const v = rc.impact.oecd[c];
              const b = v >= 4.6 ? 'Excellent' : v >= 3.6 ? 'Good' : v >= 2.6 ? 'Moderate' : 'Poor';
              return (
                <div key={c} style={{
                  padding: 10, border: `1px solid ${CRIT_COLOR[c]}33`,
                  borderTop: `3px solid ${CRIT_COLOR[c]}`, borderRadius: 8, background: 'var(--surface)',
                }} data-testid={`impact-crit-${c}`}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: CRIT_COLOR[c] }}>{c}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2 }} className="tabular">{fmt(v, 2)}</div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: bandColor(b) }}>{b}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ProvenanceFooter text={`Composed view · ${rc.source}. Inputs derive from the 186-project cohort; outputs from ${rc.outputs.length} approved KPI pillars; outcomes from the ${rc.outcomes.length} board-approved ambitions; impact from the independent OECD-DAC evaluation.`} />
    </div>
  );
}
